import React, { useEffect } from 'react';
import { string, bool, func } from 'prop-types';
import { SortAscending, SortDescending, EllipsisVertical, Null } from '@nike/nike-design-system-icons';
import { TextStyled, TextFieldStyled, ButtonStyled, useTokens } from '@nike/nike-design-system-components';
import { Box, Flex } from 'rebass';
import styled from '@emotion/styled';
import { nike } from '@nike/nike-design-tokens';

import { sortHighlightColor } from './constants';

const { podiumCdsColorTextPrimaryInverse, podiumCdsColorBorderSecondary } = nike;

const Menu = styled.div`
  background: ${podiumCdsColorTextPrimaryInverse};
  border-radius: 4px;
  box-shadow: 0 0 0 1px ${podiumCdsColorBorderSecondary};
  overflow: auto;
  position: absolute;
  right: ${({ align }) => (align === 'right' ? 'auto' : 0)};
  top: 30px;
  width: 260px;
`;

const MenuBody = styled.div`
  padding: 15px;
`;

const ColumnFilterSortMenu = ({
  align,
  disableFilter,
  disableSort,
  filterValue,
  handleClearColumnFilter,
  header,
  isFiltering,
  onFilterChange,
  onKeyDown,
  isMenuOpen,
  setIsMenuOpen,
  isEllipsisIconVisible,
  toggleSortAsc,
  toggleSortDesc,
  isSortAsc,
  isSortDesc,
  closeIconAndMenu,
  searchHelperText
}) => {
  const tokens = useTokens();
  const sortAscColor = isSortAsc ? sortHighlightColor : tokens.podiumCdsColorTextPrimary;
  const sortDescColor = isSortDesc ? sortHighlightColor : tokens.podiumCdsColorTextPrimary;
  const handleClick = event => {
    if (!event.target.closest('#columnHeaderMenu')) {
      closeIconAndMenu();
    }
  };

  useEffect(() => {
    document.body.addEventListener('click', handleClick);

    return () => {
      document.body.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <Box id='columnHeaderMenu' sx={{ position: 'relative', zIndex: '1000' }}>
      <Flex
        style={{
          height: '18px',
          width: '18px',
          visibility: isEllipsisIconVisible ? 'visible' : 'hidden',
          cursor: 'pointer'
        }}
        onClick={() => setIsMenuOpen(isOpen => !isOpen)}
      >
        <EllipsisVertical data-testid={`${header}-columnHeaderMenuIcon`} />
      </Flex>
      {isMenuOpen && (
        <Menu align={align} data-testid={`${header}-columnHeaderMenu`}>
          <MenuBody>
            <Box>
              <TextStyled appearance='body1Strong'>Sort</TextStyled>
              <hr />
            </Box>
            <Flex
              mt='10px'
              style={{ cursor: disableSort ? 'hover' : 'pointer', width: 'fit-content' }}
              onClick={disableSort ? () => {} : toggleSortAsc}
            >
              {disableSort ? (
                <Null data-testid={`${header}-sortAscDisabledIcon`} />
              ) : (
                <SortAscending color={sortAscColor} />
              )}
              <TextStyled appearance='body2' style={{ marginLeft: '5px', color: sortAscColor }}>
                Ascending
              </TextStyled>
            </Flex>
            <Flex
              mt='5px'
              style={{ cursor: disableSort ? 'hover' : 'pointer', width: 'fit-content' }}
              onClick={disableSort ? () => {} : toggleSortDesc}
            >
              {disableSort ? (
                <Null data-testid={`${header}-sortDescDisabledIcon`} />
              ) : (
                <SortDescending color={sortDescColor} />
              )}
              <TextStyled appearance='body2' style={{ marginLeft: '5px', color: sortDescColor }}>
                Descending
              </TextStyled>
            </Flex>

            <Box mt='10px'>
              <TextStyled appearance='body1Strong'>Filter</TextStyled>
              <hr />
            </Box>

            <Box mt='10px' pr='10px'>
              <TextFieldStyled
                label='Search'
                onChange={onFilterChange}
                onKeyDown={onKeyDown}
                readOnly={disableFilter}
                value={filterValue}
              />

              {searchHelperText && (
                <TextStyled appearance='body2' style={{ marginBottom: '10px' }}>
                  {searchHelperText}
                </TextStyled>
              )}
            </Box>

            {isFiltering && (
              <Flex flexDirection='row-reverse' pr='5px' pb='10px'>
                <ButtonStyled
                  appearance='text'
                  background='light'
                  ariaLabel='Clear Filter'
                  type='button'
                  onClick={handleClearColumnFilter}
                >
                  Clear Filter
                </ButtonStyled>
              </Flex>
            )}
          </MenuBody>
        </Menu>
      )}
    </Box>
  );
};

ColumnFilterSortMenu.propTypes = {
  align: string,
  closeIconAndMenu: func.isRequired,
  disableFilter: bool,
  disableSort: bool,
  filterValue: string,
  handleClearColumnFilter: func.isRequired,
  header: string,
  isEllipsisIconVisible: bool,
  isFiltering: bool,
  isMenuOpen: bool,
  isSortAsc: bool,
  isSortDesc: bool,
  onFilterChange: func.isRequired,
  onKeyDown: func.isRequired,
  searchHelperText: string,
  setIsMenuOpen: func.isRequired,
  toggleSortAsc: func.isRequired,
  toggleSortDesc: func.isRequired
};

ColumnFilterSortMenu.defaultProps = {
  align: 'left',
  disableFilter: false,
  disableSort: false,
  filterValue: '',
  header: '',
  isEllipsisIconVisible: false,
  isFiltering: false,
  isMenuOpen: false,
  isSortAsc: false,
  isSortDesc: false,
  searchHelperText: ''
};

export default ColumnFilterSortMenu;
